'use client';
import { useCallback, useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import useEmblaCarousel from 'embla-carousel-react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  EyeOff,
  Heart,
  ImageOff,
  Images,
  MapPin,
  MessageSquare,
  Phone,
} from 'lucide-react';
import { cn, formatPrice, getPropertyTypeLabel, BLUR_DATA_URL } from '@/lib/utils';
import { EASE_PREMIUM } from '@/lib/motion';

export interface PropertyCardData {
  id: string;
  title: string;
  slug: string;
  price: number;
  surface?: number;
  landSurface?: number;
  rooms: number;
  bedrooms: number;
  bathrooms: number;
  type: string;
  transactionType: string;
  isFeatured?: boolean;
  isNew?: boolean;
  priceDropPercent?: number;
  description?: string;
  city?: { name: string; slug: string };
  district?: { name: string; slug: string } | null;
  media?: Array<{ thumbnailUrl?: string; url: string }>;
  agency?: { name: string; slug?: string; phone?: string; logoUrl?: string };
}

interface PropertyCardProps {
  property: PropertyCardData;
  /**
   * - `grid` (default): 4:3 image, search-result listings.
   * - `featured`: 16:10 image, taller body, editorial sections.
   * - `compact`: square thumbnail side-by-side, sidebars / map list.
   */
  variant?: 'grid' | 'featured' | 'compact';
  priority?: boolean;
  className?: string;
  saved?: boolean;
  onToggleSave?: (next: boolean) => void;
  hidden?: boolean;
  onToggleHide?: (next: boolean) => void;
}

export function PropertyCard({
  property,
  variant = 'grid',
  priority = false,
  className,
  saved: savedProp,
  onToggleSave,
  hidden: hiddenProp,
  onToggleHide,
}: PropertyCardProps) {
  const isRent = property.transactionType === 'rent';
  const images =
    property.media
      ?.map((m) => m.thumbnailUrl || m.url)
      .filter((u): u is string => !!u) ?? [];
  const hasImages = images.length > 0;

  const [internalSaved, setInternalSaved] = useState(false);
  const isSavedControlled = onToggleSave !== undefined;
  const saved = isSavedControlled ? !!savedProp : internalSaved;

  const [internalHidden, setInternalHidden] = useState(false);
  const isHiddenControlled = onToggleHide !== undefined;
  const isHidden = isHiddenControlled ? !!hiddenProp : internalHidden;

  function handleSave(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const next = !saved;
    if (isSavedControlled) onToggleSave?.(next);
    else setInternalSaved(next);
  }

  function handleHide(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const next = !isHidden;
    if (isHiddenControlled) onToggleHide?.(next);
    else setInternalHidden(next);
  }

  if (variant === 'compact') {
    return (
      <CompactCard
        property={property}
        images={images}
        hasImages={hasImages}
        isRent={isRent}
        saved={saved}
        onSave={handleSave}
        className={className}
        priority={priority}
      />
    );
  }

  const aspect = variant === 'featured' ? 'aspect-[16/10]' : 'aspect-[4/3]';
  const pricePerSqm =
    property.surface && property.surface > 0
      ? Math.round(property.price / property.surface)
      : null;
  const locationLine = property.district?.name
    ? `${property.district.name}, ${property.city?.name ?? ''}`
    : property.city?.name ?? 'Maroc';

  return (
    <Link
      href={`/annonce/${property.slug}`}
      className={cn('group block focus:outline-none', isHidden && 'opacity-50', className)}
    >
      <article className="relative overflow-hidden rounded-md bg-surface border border-border-subtle transition-all duration-200 ease-premium hover:shadow-card hover:-translate-y-0.5 hover:border-cta/40 focus-within:ring-2 focus-within:ring-cta/40">
        {/* Image area */}
        <div className={cn('relative overflow-hidden bg-surface-cream', aspect)}>
          <CardCarousel
            images={images}
            alt={property.title}
            priority={priority}
            variant={variant === 'featured' ? 'featured' : 'grid'}
          />

          {/* Top-left status chips */}
          {(property.isNew || property.isFeatured || property.priceDropPercent) && (
            <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 max-w-[70%]">
              {property.isNew && <ChipNew>Nouveau</ChipNew>}
              {property.isFeatured && <ChipSelection>Sélection</ChipSelection>}
              {property.priceDropPercent != null && property.priceDropPercent > 0 && (
                <ChipPriceDrop>−{property.priceDropPercent}%</ChipPriceDrop>
              )}
            </div>
          )}

          {/* Top-right: favorite + (optional) hide stack */}
          <div className="absolute top-3 right-3 flex flex-col gap-2">
            <IconButton
              onClick={handleSave}
              aria-pressed={saved}
              aria-label={saved ? 'Retirer des favoris' : 'Ajouter aux favoris'}
            >
              <HeartIcon filled={saved} />
            </IconButton>
            {onToggleHide && (
              <IconButton
                onClick={handleHide}
                aria-pressed={isHidden}
                aria-label={isHidden ? 'Réafficher cette annonce' : 'Masquer cette annonce'}
              >
                <EyeOff className={cn('h-4 w-4 text-ink', isHidden && 'text-cta')} />
              </IconButton>
            )}
          </div>

          {/* Bottom-left photo count */}
          {hasImages && images.length > 1 && (
            <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-sm bg-primary/70 backdrop-blur-sm px-2 py-1 text-[11px] font-medium text-white">
              <Images className="h-3 w-3" aria-hidden />
              <span className="tabular-nums">{images.length}</span>
            </span>
          )}
        </div>

        {/* Body */}
        <div className={cn('p-4', variant === 'featured' && 'p-5')}>
          <span className="inline-flex items-center rounded-sm border border-cta/40 px-2 py-0.5 text-[11px] font-medium text-cta-hover">
            {getPropertyTypeLabel(property.type)}
          </span>

          <p
            className={cn(
              'mt-2 font-display font-semibold text-ink leading-none',
              variant === 'featured' ? 'text-3xl' : 'text-[22px]',
            )}
          >
            {formatPrice(property.price)}
            {isRent && (
              <span className="ml-1 text-sm font-normal text-ink-secondary">/&nbsp;mois</span>
            )}
          </p>

          {/* Stats row */}
          <ul className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px] text-ink-secondary">
            {(property.surface ?? 0) > 0 && (
              <Stat>
                <span className="tabular-nums">{property.surface}</span>&nbsp;m²
              </Stat>
            )}
            {(property.landSurface ?? 0) > 0 && (
              <Stat>
                <span className="tabular-nums">{property.landSurface}</span>&nbsp;m² terrain
              </Stat>
            )}
            {property.rooms > 0 && (
              <Stat>
                <span className="tabular-nums">{property.rooms}</span>&nbsp;pièces
              </Stat>
            )}
            {pricePerSqm && !isRent && (
              <Stat muted>
                <span className="tabular-nums">{formatPrice(pricePerSqm)}</span>/m²
              </Stat>
            )}
          </ul>

          <p className="mt-3 flex items-center gap-1 text-[13px] text-ink-secondary">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-ink-muted" aria-hidden />
            <span className="truncate">{locationLine}</span>
          </p>

          <h3
            className={cn(
              'mt-1 text-[13px] text-ink leading-snug line-clamp-1 transition-colors duration-200 group-hover:text-cta-hover',
              variant === 'featured' && 'text-sm',
            )}
          >
            {property.title}
          </h3>

          {property.description && (
            <p className="mt-2 text-[13px] leading-relaxed text-ink-secondary line-clamp-3">
              {property.description}
            </p>
          )}

          {property.agency && (
            <div className="mt-4 pt-3 border-t border-border-subtle flex items-center gap-3">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {property.agency.logoUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={property.agency.logoUrl}
                    alt=""
                    className="h-7 w-7 rounded-sm object-cover border border-border-subtle bg-white"
                  />
                ) : (
                  <div className="h-7 w-7 rounded-sm bg-surface-cream border border-border-subtle" aria-hidden />
                )}
                <span className="truncate text-[12px] text-ink-secondary">
                  {property.agency.name}
                </span>
              </div>
              <div className="flex items-center gap-1">
                {property.agency.phone && (
                  <a
                    href={`tel:${property.agency.phone}`}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Appeler ${property.agency.name}`}
                    className="inline-flex items-center gap-1 h-8 px-2.5 rounded-sm border border-border text-[12px] font-medium text-ink hover:border-cta hover:text-cta-hover hover:bg-cta/5 transition-colors"
                  >
                    <Phone className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Appeler</span>
                  </a>
                )}
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={`Envoyer un message à ${property.agency.name}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.location.href = `/annonce/${property.slug}#contact`;
                  }}
                  className="inline-flex items-center gap-1 h-8 px-2.5 rounded-sm bg-cta text-cta-foreground text-[12px] font-semibold hover:bg-cta-hover transition-colors cursor-pointer"
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Message</span>
                </span>
              </div>
            </div>
          )}
        </div>
      </article>
    </Link>
  );
}

// Carousel ────────────────────────────────────────────────────────────
function CardCarousel({
  images,
  alt,
  priority,
  variant,
}: {
  images: string[];
  alt: string;
  priority: boolean;
  variant: 'grid' | 'featured';
}) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, dragFree: false });
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    onSelect();
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  const scrollPrev = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      emblaApi?.scrollPrev();
    },
    [emblaApi],
  );
  const scrollNext = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      emblaApi?.scrollNext();
    },
    [emblaApi],
  );

  if (images.length === 0) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-ink-muted">
        <ImageOff className="h-10 w-10 opacity-50" />
        <span className="text-xs">Aucune photo</span>
      </div>
    );
  }

  const sizes =
    variant === 'featured'
      ? '(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 720px'
      : '(max-width: 640px) 100vw, (max-width: 1280px) 33vw, 360px';

  if (images.length === 1) {
    return (
      <div className="absolute inset-0 overflow-hidden">
        <Image
          src={images[0]}
          alt={`${alt} \u2014 photo 1`}
          fill
          sizes={sizes}
          priority={priority}
          placeholder="blur"
          blurDataURL={BLUR_DATA_URL}
          className="object-cover transition-transform duration-500 ease-premium group-hover:scale-[1.03]"
        />
      </div>
    );
  }

  return (
    <>
      <div ref={emblaRef} className="absolute inset-0 overflow-hidden">
        <div className="flex h-full">
          {images.map((src, i) => (
            <div key={i} className="relative h-full min-w-0 flex-[0_0_100%]">
              <Image
                src={src}
                alt={`${alt} \u2014 photo ${i + 1}`}
                fill
                sizes={sizes}
                priority={priority && i === 0}
                placeholder="blur"
                blurDataURL={BLUR_DATA_URL}
                className="object-cover transition-transform duration-500 ease-premium group-hover:scale-[1.03]"
              />
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={scrollPrev}
        aria-label="Photo précédente"
        disabled={selectedIndex === 0}
        className={cn(
          'absolute left-2 top-1/2 -translate-y-1/2 hidden md:inline-flex h-8 w-8 items-center justify-center rounded-full',
          'bg-white/90 text-ink shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200',
          'hover:bg-white disabled:opacity-0',
        )}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={scrollNext}
        aria-label="Photo suivante"
        disabled={selectedIndex === images.length - 1}
        className={cn(
          'absolute right-2 top-1/2 -translate-y-1/2 hidden md:inline-flex h-8 w-8 items-center justify-center rounded-full',
          'bg-white/90 text-ink shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200',
          'hover:bg-white disabled:opacity-0',
        )}
      >
        <ChevronRight className="h-4 w-4" />
      </button>

      <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex items-center gap-1">
        {images.slice(0, 6).map((_, i) => (
          <span
            key={i}
            className={cn(
              'h-1 rounded-full transition-all duration-300',
              i === selectedIndex ? 'w-4 bg-white' : 'w-1 bg-white/60',
            )}
          />
        ))}
      </div>
    </>
  );
}

// Compact (side-by-side) ──────────────────────────────────────────────
function CompactCard({
  property,
  images,
  hasImages,
  isRent,
  saved,
  onSave,
  className,
  priority,
}: {
  property: PropertyCardData;
  images: string[];
  hasImages: boolean;
  isRent: boolean;
  saved: boolean;
  onSave: (e: React.MouseEvent) => void;
  className?: string;
  priority: boolean;
}) {
  return (
    <Link
      href={`/annonce/${property.slug}`}
      className={cn(
        'group flex gap-3 p-2.5 rounded-md border border-transparent hover:border-border-subtle hover:bg-surface-cream transition-colors duration-200',
        className,
      )}
    >
      <div className="relative h-24 w-32 shrink-0 overflow-hidden rounded-sm bg-surface-cream">
        {hasImages ? (
          <Image
            src={images[0]}
            alt={property.title}
            fill
            sizes="128px"
            priority={priority}
            placeholder="blur"
            blurDataURL={BLUR_DATA_URL}
            className="object-cover transition-transform duration-300 group-hover:scale-[1.04]"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-ink-muted">
            <ImageOff className="h-6 w-6 opacity-50" />
          </div>
        )}
        <button
          onClick={onSave}
          aria-pressed={saved}
          aria-label={saved ? 'Retirer des favoris' : 'Ajouter aux favoris'}
          className="absolute top-1.5 right-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/90 shadow-sm"
        >
          <HeartIcon filled={saved} className="h-3 w-3" />
        </button>
      </div>
      <div className="min-w-0 flex-1 py-0.5">
        <p className="font-display text-base font-semibold text-ink leading-none">
          {formatPrice(property.price)}
          {isRent && (
            <span className="ml-1 text-[11px] font-normal text-ink-secondary">/mois</span>
          )}
        </p>
        <p className="mt-1 text-[12px] text-ink-secondary flex items-center gap-1">
          <MapPin className="h-3 w-3 shrink-0 text-ink-muted" />
          <span className="truncate">{property.city?.name || 'Maroc'}</span>
        </p>
        <p className="mt-1 text-[12px] text-ink leading-snug line-clamp-2 group-hover:text-cta-hover transition-colors">
          {property.title}
        </p>
      </div>
    </Link>
  );
}

// Atoms ───────────────────────────────────────────────────────────────
function IconButton({
  children,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow-sm backdrop-blur-sm',
        'transition-colors duration-200 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cta/60',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

function Stat({ children, muted = false }: { children: React.ReactNode; muted?: boolean }) {
  return (
    <li className="inline-flex items-center after:content-['·'] after:mx-1.5 after:text-ink-muted last:after:content-none">
      <span className={cn(muted && 'text-ink-muted')}>{children}</span>
    </li>
  );
}

function ChipNew({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-sm bg-cta px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-cta-foreground shadow-sm">
      {children}
    </span>
  );
}

function ChipSelection({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-sm bg-white/95 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink shadow-sm">
      {children}
    </span>
  );
}

function ChipPriceDrop({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-sm bg-error px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-error-foreground shadow-sm tabular-nums">
      {children}
    </span>
  );
}

function HeartIcon({ filled, className }: { filled: boolean; className?: string }) {
  const reduced = useReducedMotion();
  return (
    <motion.span
      initial={false}
      animate={reduced ? undefined : filled ? { scale: [1, 1.25, 1] } : { scale: 1 }}
      transition={{ duration: 0.3, ease: EASE_PREMIUM }}
      className="inline-flex"
    >
      <Heart
        className={cn(
          'h-4 w-4 transition-colors duration-200',
          filled ? 'text-error fill-error' : 'text-ink',
          className,
        )}
      />
    </motion.span>
  );
}
